import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HearingIcon from "@mui/icons-material/Hearing";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";

import {
  AMBIENT_SCRIBE_TEMPLATE_TYPE,
  type AmbientScribeTemplateType
} from "services/types/ambient-scribe-template.type";

import {
  getEffectiveTemplateType,
  getTemplateTypeLabel
} from "./scribe-template-type.util";

// Every AmbientScribeTemplateType MUST have an entry: `<Icon/>` with an undefined component takes
// the whole settings page down ("Element type is invalid"), which is exactly how the missing
// AfterVisitSummary entry surfaced when the seeded letter template first rendered here.
const TYPE_ICONS: Record<AmbientScribeTemplateType, typeof HearingIcon> = {
  [AMBIENT_SCRIBE_TEMPLATE_TYPE.CHART_NOTE]: DescriptionOutlinedIcon,
  [AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT]: HearingIcon,
  [AMBIENT_SCRIBE_TEMPLATE_TYPE.AFTER_VISIT_SUMMARY]: MailOutlineIcon
};

type ScribeTemplateTypeLabelProps = {
  type: AmbientScribeTemplateType | null | undefined | string;
  /** Typography variant for the label text. */
  variant?: "body1" | "body2" | "caption";
  /** Dims the icon + text — used for the secondary line of a dropdown option. */
  muted?: boolean;
  sx?: SxProps;
};

/**
 * Icon + label for a template type. Always renders something: an unset type resolves to
 * "Chart Note" via `getEffectiveTemplateType`, matching the backend's `null ≡ ChartNote`.
 */
export function ScribeTemplateTypeLabel({
  type,
  variant = "body1",
  muted = false,
  sx
}: Readonly<ScribeTemplateTypeLabelProps>) {
  const effectiveType = getEffectiveTemplateType(type);
  const Icon = TYPE_ICONS[effectiveType];
  const iconSize = variant === "caption" ? 14 : 18;

  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={sx}>
      <Icon
        sx={{
          fontSize: iconSize,
          color: muted ? "text.secondary" : "action.active",
          flexShrink: 0
        }}
      />
      <Typography
        variant={variant}
        component="span"
        color={muted ? "text.secondary" : undefined}
      >
        {getTemplateTypeLabel(effectiveType)}
      </Typography>
    </Stack>
  );
}

type AppointmentTypeDotProps = {
  color: string | null | undefined;
  size?: number;
};

/**
 * The appointment-type colour swatch. Mirrors the long-standing inline pattern in
 * `view-schedule-items-table.component.tsx` — the 1px border is load-bearing, without it
 * a white or very pale appointment-type colour is invisible on a white row.
 *
 * Renders nothing when there is no colour, rather than an empty circle.
 */
export function AppointmentTypeDot({
  color,
  size = 12
}: Readonly<AppointmentTypeDotProps>) {
  if (!color) return null;

  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        background: color,
        borderRadius: "50%",
        border: `solid 1px ${grey[400]}`,
        flexShrink: 0,
        display: "inline-block"
      }}
    />
  );
}
